/**
 * Tasks — To-do list with status, priority, and due dates
 */

import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import RichTextEditor from '../RichTextEditor';
import { authFetch } from '../../auth';
import GoogleSyncCard from '../GoogleSync/GoogleSyncCard';
import './TasksPage.css';

const STATUSES = ['pending', 'in_progress', 'done'];
const STATUS_LABELS = { pending: 'To Do', in_progress: 'In Progress', done: 'Done' };
const PRIORITIES = ['high', 'medium', 'low'];
const PRIORITY_LABELS = { high: 'High', medium: 'Medium', low: 'Low' };

const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('pending');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [taskTags, setTaskTags] = useState('');
  const [saving, setSaving] = useState(false);

  // Agent assignment
  const [assigneeType, setAssigneeType] = useState('none'); // 'none' | 'user' | 'agent'
  const [agentSchedule, setAgentSchedule] = useState('');
  const [agentCron, setAgentCron] = useState('');
  const [agentTimezone, setAgentTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [agentInstructions, setAgentInstructions] = useState('');
  const [agentTools, setAgentTools] = useState([]);
  const [agentMaxRuns, setAgentMaxRuns] = useState('1'); // default 1 run
  const [agentEnabled, setAgentEnabled] = useState(true);
  const [showAgentPanel, setShowAgentPanel] = useState(false);
  const [availableTools, setAvailableTools] = useState([]);

  // Fetch available AI tools
  var loadAvailableTools = async function() {
    try {
      var res = await authFetch('/api/aiassistant/config');
      if (res.ok) {
        var data = await res.json();
        var tools = data.config && data.config.access ? Object.keys(data.config.access) : [];
        setAvailableTools(tools);
      }
    } catch(e) {}
  };

  var DEFAULT_FRONTMATTER = '---\nname: my-task\ndescription: What this task does\nschedule: every weekday at 9am\ntimezone: UTC\ntools: []\nmax_runs: null\n---\n\nYour instructions here...';

  // Simple natural language → cron parser (client-side)
  var parseScheduleLocal = function(text) {
    if (!text || !text.trim()) return '';
    var s = text.toLowerCase().trim();
    var minute = '0', hour = '9', dom = '*', month = '*', dow = '*';
    var timeMatch = s.match(/at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i) || s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i);
    if (timeMatch) { hour = String(parseInt(timeMatch[1])); minute = timeMatch[2] || '0'; var ap = timeMatch[3]; if (ap === 'pm' && parseInt(hour) < 12) hour = String(parseInt(hour) + 12); if (ap === 'am' && parseInt(hour) === 12) hour = '0'; }
    else if (s.indexOf('noon') !== -1) { hour = '12'; minute = '0'; }
    else if (s.indexOf('midnight') !== -1) { hour = '0'; minute = '0'; }
    if (s.match(/weekdays?\b|workdays?\b/)) dow = '1-5';
    else if (s.match(/every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i)) { var ds = { sunday:'0', monday:'1', tuesday:'2', wednesday:'3', thursday:'4', friday:'5', saturday:'6' }; var dm = s.match(/every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i); if (dm) dow = ds[dm[1]]; }
    else if (s.match(/weekly\s*on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i)) { var ds2 = { sunday:'0', monday:'1', tuesday:'2', wednesday:'3', thursday:'4', friday:'5', saturday:'6' }; var dm2 = s.match(/weekly\s*on\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i); if (dm2) dow = ds2[dm2[1]]; }
    else if (s.match(/every\s+(\d+)\s*hours?/i)) { var hm = s.match(/every\s+(\d+)\s*hours?/i); return '0 */' + hm[1] + ' * * *'; }
    else if (s.match(/every\s+(\d+)\s*mins?/i)) { var mm = s.match(/every\s+(\d+)\s*mins?/i); return '*/' + mm[1] + ' * * * *'; }
    else if (s.indexOf('hourly') !== -1) return '0 * * * *';
    else if (s.indexOf('monthly') !== -1) { var dma = s.match(/(\d{1,2})(?:st|nd|rd|th)?/); dom = dma ? dma[1] : '1'; }
    if (s.indexOf('daily') === -1 && s.indexOf('every day') === -1 && s.indexOf('everyday') === -1 && dow === '*' && s.indexOf('every') === -1) return '';
    return minute + ' ' + hour + ' ' + dom + ' ' + month + ' ' + dow;
  };

  // Update tools in frontmatter without touching instructions body
  var updateFrontmatterTools = function(frontmatter, tools) {
    var lines = (frontmatter || '').split('\n');
    var inFm = false; var fmEnd = 0; var hasTools = false;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].trim() === '---') {
        if (!inFm) { inFm = true; } else { fmEnd = i; break; }
      } else if (inFm && lines[i].startsWith('tools:')) {
        lines[i] = 'tools: [' + tools.join(', ') + ']';
        hasTools = true;
      }
    }
    if (!hasTools && fmEnd > 0) {
      lines.splice(fmEnd, 0, 'tools: [' + tools.join(', ') + ']');
    }
    return lines.join('\n');
  };

  // Sync frontmatter changes → GUI fields and GUI fields → frontmatter
  var syncFmToGui = function(frontmatter) {
    if (!frontmatter) return;
    var lines = frontmatter.split('\n');
    var inFm = false;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].trim() === '---') { inFm = !inFm; continue; }
      if (!inFm) continue;
      var m = lines[i].match(/^(\w+):\s*(.+)/);
      if (!m) continue;
      var key = m[1], val = m[2].trim();
      if (key === 'schedule') { setAgentSchedule(val); setAgentCron(parseScheduleLocal(val)); }
      else if (key === 'timezone') setAgentTimezone(val);
      else if (key === 'max_runs' || key === 'maxRuns') { var v = val.replace(/['"]/g,''); setAgentMaxRuns(v === 'null' || v === '' || v === '0' ? '' : v); }
      else if (key === 'tools') {
        var toolsMatch = val.match(/\[([^\]]*)\]/);
        if (toolsMatch) { var tools = toolsMatch[1].split(',').map(function(t) { return t.trim().replace(/['"]/g,''); }).filter(Boolean); setAgentTools(tools); }
      }
    }
  };

  var syncGuiToFm = function(fm) {
    var lines = (fm || '').split('\n');
    var inFm = false; var fmEnd = 0;
    var fields = { schedule: false, timezone: false, tools: false, max_runs: false };
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].trim() === '---') { if (!inFm) inFm = true; else { fmEnd = i; break; } continue; }
      if (!inFm) continue;
      if (lines[i].startsWith('schedule:')) { lines[i] = 'schedule: ' + agentSchedule; fields.schedule = true; }
      else if (lines[i].startsWith('timezone:')) { lines[i] = 'timezone: ' + agentTimezone; fields.timezone = true; }
      else if (lines[i].startsWith('tools:')) { lines[i] = 'tools: [' + agentTools.join(', ') + ']'; fields.tools = true; }
      else if (lines[i].startsWith('max_runs:') || lines[i].startsWith('maxRuns:')) { lines[i] = 'max_runs: ' + (agentMaxRuns || '0'); fields.max_runs = true; }
    }
    if (fmEnd > 0) {
      if (!fields.schedule) lines.splice(fmEnd, 0, 'schedule: ' + agentSchedule);
      if (!fields.timezone) lines.splice(fmEnd, 0, 'timezone: ' + agentTimezone);
      if (!fields.tools) lines.splice(fmEnd, 0, 'tools: [' + agentTools.join(', ') + ']');
      if (!fields.max_runs) lines.splice(fmEnd, 0, 'max_runs: ' + (agentMaxRuns || '0'));
    }
    return lines.join('\n');
  };

  const loadTasks = useCallback(async () => {
    try {
      let url = '/api/tasks';
      const params = [];
      if (filter !== 'all') params.push(`status=${filter}`);
      if (search) params.push(`q=${encodeURIComponent(search)}`);
      if (params.length) url += '?' + params.join('&');
      const res = await authFetch(url);
      if (res.ok) setTasks(await res.json());
    } catch (e) {
      console.error('Failed to load tasks:', e);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const openNew = () => {
    setEditing({ _new: true });
    setRunState({ stage: 'idle', output: '', ms: 0 });
    setTitle('');
    setDescription('');
    setStatus('pending');
    setPriority('medium');
    setDueDate('');
    setTaskTags('');
    setAssigneeType('none');
    setAgentSchedule('');
    setAgentCron('');
    setAgentInstructions('');
    setAgentMaxRuns('');
    setAgentEnabled(true);
    setShowAgentPanel(false);
  };

  const openEdit = (task) => {
    setEditing(task);
    setRunState({ stage: 'idle', output: '', ms: 0 });
    setTitle(task.title || '');
    setDescription(task.description || '');
    setStatus(task.status || 'pending');
    setPriority(task.priority || 'medium');
    setDueDate(task.dueDate ? task.dueDate.slice(0, 10) : '');
    setTaskTags((task.tags || []).join(', '));
    // Agent fields
    var aa = task.assignee || {};
    if (aa && aa.type === 'agent') {
      setAssigneeType('agent');
      var spec = task.agentSpec || {};
      setAgentSchedule(spec.schedule || '');
      setAgentCron(spec.cronExpr || '');
      setAgentTimezone(spec.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setAgentInstructions(spec.instructions || '');
      setAgentTools(spec.tools || []);
      var mr = spec.maxRuns;
      setAgentMaxRuns(mr != null && mr !== 0 ? String(mr) : ''); // empty = forever
      setAgentEnabled(spec.enabled !== false);
      setShowAgentPanel(true);
    } else {
      setAssigneeType('none');
    setAgentSchedule('');
      setAgentCron('');
      setAgentInstructions('');
    setAgentMaxRuns('1');
      setAgentEnabled(true);
      setShowAgentPanel(false);
    }
  };

  const buildPayload = () => {
    const payload = {
      title,
      description,
      status,
      priority,
      dueDate: dueDate || null,
      tags: taskTags.split(',').map(t => t.trim()).filter(Boolean)
    };
    // Agent assignment
    if (assigneeType === 'agent') {
      payload.assignee = { type: 'agent', agentId: 'openclaw' };
      payload.agentSpec = {
        schedule: agentSchedule,
        cronExpr: agentCron,
        timezone: agentTimezone,
        instructions: agentInstructions,
        tools: agentTools,
        maxRuns: agentMaxRuns ? parseInt(agentMaxRuns) : 0, // 0 = forever
        enabled: agentEnabled,
        sandbox: { enabled: true } // always sandboxed
      };
    } else {
      payload.assignee = null;
      payload.agentSpec = null;
    }
    return payload;
  };

  // Persist WITHOUT closing the dialog — Run Now in create/edit mode must save
  // first (the server needs the stored agent assignee/owner) but Save's
  // setEditing(null) closes the form, which is exactly the UX being reported.
  const persistQuiet = async () => {
    if (!title.trim()) return null;
    const payload = buildPayload();
    if (editing._new) {
      const res = await authFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); alert('Error: ' + (d.error || 'Could not save task')); return null; }
      const created = await res.json();
      setTasks(prev => [created, ...prev]);
      setEditing(Object.assign({}, editing, { uid: created.uid, _new: false }));
      return created.uid;
    }
    const res = await authFetch(`/api/tasks/${editing.uid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!res.ok) { const d = await res.json().catch(() => ({})); alert('Error: ' + (d.error || 'Could not save task')); return null; }
    setTasks(prev => prev.map(t => t.uid === editing.uid ? { ...t, ...payload, updatedAt: new Date().toISOString() } : t));
    return editing.uid;
  };

  // Run feedback: instant acknowledgment → inline result (no blocking alert).
  const [runState, setRunState] = useState({ stage: 'idle', output: '', ms: 0 });

  const save = async () => {
    if (!title.trim()) return;
    setSaving(true);
    const payload = buildPayload();

    try {
      if (editing._new) {
        const res = await authFetch('/api/tasks', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const created = await res.json();
          setTasks(prev => [created, ...prev]);
        }
      } else {
        const res = await authFetch(`/api/tasks/${editing.uid}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          setTasks(prev => prev.map(t => t.uid === editing.uid ? { ...t, ...payload, updatedAt: new Date().toISOString() } : t));
        }
      }
      setEditing(null);
    } catch (e) {
      console.error('Save failed:', e);
    } finally {
      setSaving(false);
    }
  };

  const deleteTask = async (uid) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      const res = await authFetch(`/api/tasks/${uid}`, { method: 'DELETE' });
      if (res.ok) setTasks(prev => prev.filter(t => t.uid !== uid));
      if (editing && editing.uid === uid) setEditing(null);
    } catch (e) {}
  };

  const toggleStatus = async (task) => {
    const nextIdx = (STATUSES.indexOf(task.status) + 1) % STATUSES.length;
    const nextStatus = STATUSES[nextIdx];
    try {
      await authFetch(`/api/tasks/${task.uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus })
      });
      setTasks(prev => prev.map(t => t.uid === task.uid ? { ...t, status: nextStatus } : t));
    } catch (e) {}
  };

  const counts = {
    all: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    in_progress: tasks.filter(t => t.status === 'in_progress').length,
    done: tasks.filter(t => t.status === 'done').length
  };

  const formatDue = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    const today = new Date();
    today.setHours(0,0,0,0);
    const diff = Math.floor((dt - today) / 86400000);
    if (diff < 0) return 'Overdue';
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    return dt.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) return <div className="tasks-loading">Loading tasks...</div>;

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <h2>Tasks</h2>
        <div className="tasks-actions">
          <div className="tasks-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            <input type="text" placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <GoogleSyncCard services={['tasks']} />
          <button className="btn-new-task" onClick={openNew}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Task
          </button>
        </div>
      </div>

      <div className="tasks-filters">
        {['all', ...STATUSES].map(f => (
          <button
            key={f}
            className={`filter-btn ${filter === f ? 'active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'All' : STATUS_LABELS[f]} <span className="filter-count">{counts[f]}</span>
          </button>
        ))}
      </div>

      {tasks.length === 0 && !editing && (
        <div className="tasks-empty">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#c4a882" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
          <p>No tasks yet. Add your first task!</p>
        </div>
      )}

      <div className="tasks-list">
        {tasks.map(task => (
          <div key={task.uid} className={`task-item ${task.status}`}>
            <button className={`task-check ${task.status}`} onClick={() => toggleStatus(task)} title={`Status: ${STATUS_LABELS[task.status] || task.status}`}>
              {task.status === 'done' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              ) : task.status === 'in_progress' ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
              )}
            </button>
            <div className="task-content" onClick={() => openEdit(task)}>
              <div className="task-title-row">
                <span className={`task-title ${task.status === 'done' ? 'done' : ''}`}>{task.title}</span>
                <span className={`priority-badge ${task.priority}`}>{PRIORITY_LABELS[task.priority] || task.priority}</span>
              </div>
              {task.description && <p className="task-desc"><ReactMarkdown>{task.description.length > 100 ? task.description.substring(0, 100) + '...' : task.description}</ReactMarkdown></p>}
              <div className="task-meta">
                {task.dueDate && <span className={`task-due ${formatDue(task.dueDate) === 'Overdue' ? 'overdue' : ''}`}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/></svg>
                  {formatDue(task.dueDate)}
                </span>}
                {(task.tags || []).map(t => <span key={t} className="task-tag">{t}</span>)}
              </div>
            </div>
            <button className="btn-delete-task" onClick={() => deleteTask(task.uid)} title="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            </button>
          </div>
        ))}
      </div>

      {/* Edit/Create Modal */}
      {editing && (
        <div className="task-modal-overlay">
          <div className="task-modal" onClick={e => e.stopPropagation()}>
            <div className="task-modal-header">
              <h3>{editing._new ? 'New Task' : 'Edit Task'}</h3>
              <button className="btn-close-modal" onClick={() => setEditing(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="task-modal-body">
              {runState.stage !== 'idle' && (
                <div className={'run-status-card ' + runState.stage}
                     data-testid={runState.stage === 'running' ? 'run-status-card' : 'run-result-card'}>
                  {runState.stage === 'running' ? (
                    <><span className="rs-spin"></span>
                      <div><b>Running — the agent is working…</b>
                        <div className="rs-sub">Request sent instantly · the dialog stays open.</div></div>
                    </>
                  ) : runState.stage === 'done' ? (
                    <><span className="rs-ic">✅</span>
                      <div><b>Run completed — {(runState.ms / 1000).toFixed(1)}s</b>
                        <div className="rs-out">{runState.output || 'Finished (no output text).'}</div></div>
                    </>
                  ) : (
                    <><span className="rs-ic">⚠️</span>
                      <div><b>Run failed</b>
                        <div className="rs-out">{runState.output}</div></div>
                    </>
                  )}
                </div>
              )}
              <input
                className="task-title-input"
                type="text"
                placeholder="Task title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                autoFocus
              />
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Description (optional)"
                rows={5}
              />
              <div className="task-fields-row">
                <div className="task-field">
                  <label>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)}>
                    {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                  </select>
                </div>
                <div className="task-field">
                  <label>Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
                  </select>
                </div>
                <div className="task-field">
                  <label>Due Date</label>
                  <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
                </div>
              </div>
              {/* Agent Assignment */}
              <div className="task-field">
                <label>Assignee</label>
                <select value={assigneeType} onChange={function(e) { setAssigneeType(e.target.value); setShowAgentPanel(e.target.value === 'agent'); if (e.target.value === 'agent' && !agentInstructions) { setAgentInstructions(DEFAULT_FRONTMATTER); loadAvailableTools(); } }}>
                  <option value="none">None</option>
                  <option value="agent">🤖 AI Agent</option>
                </select>
              </div>

              {/* Agent Execution Panel */}
              {assigneeType === 'agent' && (
                <div className="task-agent-panel">
                  <div className="task-agent-header" onClick={function() { setShowAgentPanel(function(v) { return !v; }); }}>
                    <span>⚙ Agent Execution</span>
                    <span className="task-agent-toggle">{showAgentPanel ? '▼' : '▶'}</span>
                  </div>
                  {showAgentPanel && (
                    <div className="task-agent-body">
                      <div className="task-field">
                        <label>Schedule</label>
                        <input type="text" value={agentSchedule} onChange={function(e) { setAgentSchedule(e.target.value); setAgentCron(parseScheduleLocal(e.target.value)); setAgentInstructions(syncGuiToFm(agentInstructions)); }} placeholder="e.g. every weekday at 9am, daily at 2pm" />
                        {agentCron ? <div className="task-cron-preview">cron: <code>{agentCron}</code></div> : <div className="task-cron-preview" style={{color:'#888'}}>No cron set — will run once on due date if set, or manually only</div>}
                        {agentCron && <div className="task-cron-preview" style={{color:'#888',fontSize:10}}>Also set a due date above for one-time execution</div>}
                      </div>
                      <div className="task-fields-row">
                        <div className="task-field">
                          <label>Timezone</label>
                          <input type="text" value={agentTimezone} onChange={function(e) { setAgentTimezone(e.target.value); setAgentInstructions(syncGuiToFm(agentInstructions)); }} placeholder="America/New_York" />
                        </div>
                        <div className="task-field">
                          <label>Max Runs</label>
                          <input type="text" value={agentMaxRuns} onChange={function(e) { setAgentMaxRuns(e.target.value); setAgentInstructions(syncGuiToFm(agentInstructions)); }} placeholder="forever" />
                        </div>
                      </div>
                      <div className="task-field">
                        <label>Instructions (YAML frontmatter + Markdown body)</label>
                        <RichTextEditor value={agentInstructions} onChange={function(val) { setAgentInstructions(val); syncFmToGui(val); }} placeholder={DEFAULT_FRONTMATTER} rows={6} />
                      </div>
                      {availableTools.length > 0 && (
                        <div className="task-field">
                          <label>Tools (select which tools the agent may use)</label>
                          <div className="task-tools-grid">
                            <button className="task-tools-infer" onClick={function() { var txt = (title + ' ' + description).toLowerCase(); var inferred = availableTools.filter(function(t) { return txt.indexOf(t) !== -1; }); if (inferred.length > 0) setAgentTools(inferred.slice(0, 5)); else setAgentTools(availableTools.slice(0, 3)); }} title="Auto-suggest tools from content">✨ Infer from description</button>
                            {availableTools.map(function(t) { return (
                              <label key={t} className="task-tool-check">
                                <input type="checkbox" checked={agentTools.includes(t)} onChange={function(e) { var newTools = e.target.checked ? agentTools.concat([t]) : agentTools.filter(function(x) { return x !== t; }); setAgentTools(newTools); setAgentInstructions(updateFrontmatterTools(agentInstructions, newTools)); }} />
                                {t}
                              </label>
                            ); })}
                          </div>
                        </div>
                      )}
                      <label style={{fontSize:12,fontWeight:500,color:'#555',marginTop:6,display:'flex',alignItems:'center',gap:8}}>
                        <input type="checkbox" checked={agentEnabled} onChange={function(e) { setAgentEnabled(e.target.checked); }} />
                        Enabled
                      </label>
                      {editing && (
                        <button className="btn-run-now" data-testid="task-run-now"
                          disabled={runState.stage === 'running'}
                          onClick={async function() {
                            if (runState.stage === 'running') return;
                            var t0 = Date.now();
                            setRunState({ stage: 'running', output: '', ms: 0 }); // instant ack, don't await the run
                            var toText = function(v) { return typeof v === 'object' && v ? (v.message || v.error && v.error.message || JSON.stringify(v)) : String(v || ''); };
                            try {
                              var runUid = await persistQuiet();
                              if (!runUid) { setRunState({ stage: 'error', output: 'Could not save the task first.', ms: 0 }); return; }
                              // Baseline FIRST — a fast run can finish before the first poll,
                              // which would make the count comparison miss the new entry.
                              var snapshotLen = 0;
                              try {
                                var lr0 = await authFetch('/api/tasks?limit=50');
                                var a0 = await lr0.json();
                                var l0 = Array.isArray(a0) ? a0 : (a0.tasks || []);
                                var t0row = l0.find(function(x) { return x.uid === runUid; });
                                snapshotLen = ((t0row && t0row.agentSpec && t0row.agentSpec.runHistory) || []).length;
                              } catch (e0) { snapshotLen = 0; }
                              // Async: server answers 202 instantly (long runs survive the
                              // gateway timeout) and writes runHistory when done — poll for it.
                              await authFetch('/api/tasks/' + runUid + '/run', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ async: true }) });
                              var poll = setInterval(async function() {
                                try {
                                  var lr = await authFetch('/api/tasks?limit=50');
                                  var arr = await lr.json();
                                  var list = Array.isArray(arr) ? arr : (arr.tasks || []);
                                  var t = list.find(function(x) { return x.uid === runUid; });
                                  var hist = (t && t.agentSpec && t.agentSpec.runHistory) || [];
                                  if (hist.length > snapshotLen) {
                                    clearInterval(poll);
                                    var last = hist[hist.length - 1];
                                    if (last && last.status === 'error') setRunState({ stage: 'error', output: toText(last.error), ms: Date.now() - t0 });
                                    else setRunState({ stage: 'done', output: String((last && last.output) || '').substring(0, 600), ms: Date.now() - t0 });
                                  } else if (Date.now() - t0 > 240000) {
                                    clearInterval(poll);
                                    setRunState({ stage: 'error', output: 'Still running after 4 min — check the run history shortly.', ms: 0 });
                                  }
                                } catch (pollErr) { /* transient list fetch failure — keep polling */ }
                              }, 3000);
                            } catch(e) { setRunState({ stage: 'error', output: toText(e.message), ms: Date.now() - t0 }); }
                          }}
                          style={{marginTop:8,padding:'6px 12px',background:'#7b1fa2',color:'#fff',border:'none',borderRadius:6,cursor:'pointer',fontSize:12,opacity:runState.stage==='running'?0.6:1}}>
                          {runState.stage === 'running' ? '⏳ Running…' : '▶ Run Now'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="task-field">
                <label>Tags (comma-separated)</label>
                <input type="text" value={taskTags} onChange={e => setTaskTags(e.target.value)} placeholder="work, personal, urgent" />
              </div>
            </div>
            <div className="task-modal-footer">
              <button className="btn-cancel" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-save-task" onClick={save} disabled={saving || !title.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TasksPage;
